import { useEffect, useMemo, useState } from "react";
import { Plus, ShieldAlert, X } from "lucide-react";
import type { FlightOccurrenceEvent, FlightOccurrenceEventKind, FlightOccurrenceStatus, FlightPlan, SaveGame, ScheduleSource, ServiceDay, Severity, WorldData } from "../../../domain/types";
import { SERVICE_DAY_LABELS } from "../../../domain/rules/schedule";
import { currentGroundOpsForFlight } from "../../../domain/simulation";
import { AirportSearch } from "../../components/AirportSearch";
import { Select } from "../../components/Panel";
import { formatProgramStatus, formatSeverity } from "../../format";
import { FilterAll, OCCURRENCE_EVENT_KIND_VALUES, OCCURRENCE_TRIGGER_REFERENCE_VALUES, SEVERITY_VALUES } from "./gmTypes";
import { MiniStat, ServiceDayToggleRow, formatFlightPlanLabel, formatOccurrenceEventSummary, formatOccurrenceKind, formatOccurrenceLabel, formatOccurrenceStatus, formatOccurrenceTriggerReference, formatServiceDays, matchesQuery } from "./GmShared";


export function FlightPlanPanel({
  save,
  world,
  schedule,
  onCreateFlightPlan,
  onCancelFlightPlan,
  onCancelFlightLeg,
  onCreateFlightOccurrenceEvent
}: {
  save: SaveGame | null;
  world: WorldData;
  schedule: ScheduleSource;
  onCreateFlightPlan: (input: { flightNo: string; origin: string; destination: string; serviceDays: ServiceDay[]; startsDay: number; endsDay?: number; departureTimeLocal: string; minimumTurnMinutes: number; priority: number }) => void;
  onCancelFlightPlan: (flightPlanId: string, reason: string) => void;
  onCancelFlightLeg: (input: { occurrenceId: string; day: number; dateIso: string; reason: string }) => void;
  onCreateFlightOccurrenceEvent: (input: Omit<FlightOccurrenceEvent, "id" | "status" | "createdBy" | "createdAt">) => void;
}) {
  const [draft, setDraft] = useState({
    flightNo: "AO510",
    origin: "PEK",
    destination: "SIN",
    serviceDays: ["mon", "wed", "fri"] as ServiceDay[],
    startsDay: 1,
    endsDay: 0,
    departureTimeLocal: "09:20",
    minimumTurnMinutes: 70,
    priority: 80
  });
  const [flightPlanId, setFlightPlanId] = useState(schedule.flightPlans[0]?.id ?? "");
  const [planQuery, setPlanQuery] = useState("");
  const [planCancelReason, setPlanCancelReason] = useState("GM 停用未来航班计划");
  const [occurrenceId, setOccurrenceId] = useState(schedule.occurrences[0]?.id ?? "");
  const [occurrenceQuery, setOccurrenceQuery] = useState("");
  const [occurrenceStatus, setOccurrenceStatus] = useState<FilterAll<FlightOccurrenceStatus>>("all");
  const [cancelDay, setCancelDay] = useState(1);
  const [cancelDateIso, setCancelDateIso] = useState("2027-04-01");
  const [legCancelReason, setLegCancelReason] = useState("GM 航段取消");
  const [occurrenceEventDraft, setOccurrenceEventDraft] = useState({
    title: "低能见度临时检查",
    kind: "airport_visibility" as FlightOccurrenceEvent["kind"],
    severity: "warning" as Severity,
    trigger: {
      reference: "takeoff" as NonNullable<FlightOccurrenceEvent["trigger"]>["reference"],
      offsetMinutes: 30
    },
    description: "目的地天气短时恶化，玩家需自行判断等待、备降、取消或继续尝试。",
    reason: "GM 指定今日航班突发",
    riskProfile: {
      delayMinutesMin: 45,
      delayMinutesMax: 160,
      diversionRisk: 0.35,
      newsRisk: 0.22,
      cancellationPressure: 0.18
    }
  });
  const activeFlightPlan = schedule.flightPlans.find((plan) => plan.id === flightPlanId) ?? schedule.flightPlans[0];
  const activeFlightPlanId = activeFlightPlan?.id ?? "";
  const firstUnopenedOccurrenceDay = gmFirstUnopenedOccurrenceDay(save);
  const unstartedOccurrences = useMemo(
    () => schedule.occurrences.filter((occurrence) => isUnstartedOccurrence(occurrence, firstUnopenedOccurrenceDay)),
    [schedule.occurrences, firstUnopenedOccurrenceDay]
  );
  const activeOccurrence = unstartedOccurrences.find((occurrence) => occurrence.id === occurrenceId) ?? unstartedOccurrences[0];
  const activeOccurrenceId = activeOccurrence?.id ?? "";
  const activeOccurrenceInTargetWindow = !activeOccurrence || isUnstartedOccurrence(activeOccurrence, firstUnopenedOccurrenceDay);
  const activeOccurrenceEventInTargetWindow = activeOccurrenceInTargetWindow;
  const activePlans = schedule.flightPlans.filter((plan) => plan.active);
  const cancelledOccurrences = schedule.occurrences.filter((occurrence) => occurrence.status === "cancelled");
  const activeOccurrenceEvents = schedule.occurrenceEvents.filter((event) => event.status === "active");
  const activeOpsFlights = save?.activeOpsDay?.flights ?? [];
  const awaitingReleaseCount = activeOpsFlights.filter((flight) => flight.status === "awaiting_release" || flight.status === "delayed" || flight.status === "needs_recovery").length;
  const releasedCount = activeOpsFlights.filter((flight) => flight.status === "released" || flight.status === "airborne").length;
  const groundOpsActiveCount = activeOpsFlights.filter((flight) => currentGroundOpsForFlight(flight)?.status === "in_progress").length;
  const groundOpsBlockedCount = activeOpsFlights.filter((flight) => currentGroundOpsForFlight(flight)?.nodes.some((node) => node.status === "blocked")).length;
  const cancellationByOccurrence = new Map(schedule.cancellations.map((cancellation) => [cancellation.occurrenceId, cancellation]));
  const filteredFlightPlans = schedule.flightPlans.filter((plan) =>
    matchesQuery([plan.flightNo, plan.origin, plan.destination, formatServiceDays(plan.serviceDays), plan.cancellationReason], planQuery)
  );
  const filteredOccurrences = schedule.occurrences
    .filter((occurrence) => occurrenceStatus === "all" || occurrence.status === occurrenceStatus)
    .filter((occurrence) =>
      matchesQuery(
        [
          occurrence.flightNo,
          occurrence.origin,
          occurrence.destination,
          occurrence.dateIso,
          occurrence.day,
          occurrence.status,
          SERVICE_DAY_LABELS[occurrence.serviceDay],
          occurrence.cancellationReason,
          cancellationByOccurrence.get(occurrence.id)?.reason
        ],
        occurrenceQuery
      )
    )
    .slice(0, 120);

  const selectOccurrence = (nextOccurrenceId: string) => {
    setOccurrenceId(nextOccurrenceId);
    const next = schedule.occurrences.find((occurrence) => occurrence.id === nextOccurrenceId);
    if (next) {
      setCancelDay(next.day);
      setCancelDateIso(next.dateIso);
    }
  };

  useEffect(() => {
    if (unstartedOccurrences.length === 0 || unstartedOccurrences.some((occurrence) => occurrence.id === occurrenceId)) {
      return;
    }
    const next = unstartedOccurrences[0];
    setOccurrenceId(next.id);
    setCancelDay(next.day);
    setCancelDateIso(next.dateIso);
  }, [unstartedOccurrences, occurrenceId]);

  const clonePlanToDraft = (plan: FlightPlan) => {
    setFlightPlanId(plan.id);
    setDraft({
      flightNo: plan.flightNo,
      origin: plan.origin,
      destination: plan.destination,
      serviceDays: plan.serviceDays,
      startsDay: plan.startsDay,
      endsDay: plan.endsDay ?? 0,
      departureTimeLocal: plan.departureTimeLocal,
      minimumTurnMinutes: plan.minimumTurnMinutes,
      priority: plan.priority
    });
  };

  return (
    <div className="gm-flight-program">
      <div className="gm-kpi-strip">
        <MiniStat label="运行中计划" value={activePlans.length} />
        <MiniStat label="未来实例" value={schedule.occurrences.length} />
        <MiniStat label="已取消实例" value={cancelledOccurrences.length} tone={cancelledOccurrenceTone(cancelledOccurrences.length)} />
        <MiniStat label="活动单班事件" value={activeOccurrenceEvents.length} tone={activeOccurrenceEventTone(activeOccurrenceEvents.length)} />
        <MiniStat label="玩家待放行" value={awaitingReleaseCount} tone={awaitingReleaseTone(awaitingReleaseCount)} />
        <MiniStat label="已放行/空中" value={releasedCount} tone={releasedFlightTone(releasedCount)} />
        <MiniStat label="地服执行中" value={groundOpsActiveCount} tone={groundOpsActiveTone(groundOpsActiveCount)} />
        <MiniStat label="地服阻断" value={groundOpsBlockedCount} tone={groundOpsBlockedTone(groundOpsBlockedCount)} />
      </div>

      <div className="gm-flight-command-grid">
        <section className="gm-form-section gm-form-section-primary">
          <strong>新航班计划</strong>
          <div className="gm-flight-form">
            <label>
              航班号
              <input value={draft.flightNo} onChange={(event) => setDraft((current) => ({ ...current, flightNo: event.target.value.toUpperCase() }))} />
            </label>
            <AirportSearch id="gm-flight-origin" label="起飞机场" world={world} value={draft.origin} onChange={(origin) => setDraft((current) => ({ ...current, origin }))} />
            <AirportSearch id="gm-flight-destination" label="到达机场" world={world} value={draft.destination} onChange={(destination) => setDraft((current) => ({ ...current, destination }))} />
            <ServiceDayToggleRow value={draft.serviceDays} onChange={(serviceDays) => setDraft((current) => ({ ...current, serviceDays }))} />
            <label>
              起飞时刻
              <input type="time" value={draft.departureTimeLocal} onChange={(event) => setDraft((current) => ({ ...current, departureTimeLocal: event.target.value }))} />
            </label>
            <label>
              开始运行日
              <input type="number" min={1} value={draft.startsDay} onChange={(event) => setDraft((current) => ({ ...current, startsDay: Number(event.target.value) }))} />
            </label>
            <label>
              结束运行日
              <input type="number" min={0} value={draft.endsDay} onChange={(event) => setDraft((current) => ({ ...current, endsDay: Number(event.target.value) }))} />
            </label>
            <label>
              最小过站
              <input type="number" min={0} value={draft.minimumTurnMinutes} onChange={(event) => setDraft((current) => ({ ...current, minimumTurnMinutes: Number(event.target.value) }))} />
            </label>
            <label>
              优先级
              <input type="number" min={1} max={100} value={draft.priority} onChange={(event) => setDraft((current) => ({ ...current, priority: Number(event.target.value) }))} />
            </label>
            <button
              className="primary-command"
              onClick={() =>
                onCreateFlightPlan({
                  ...draft,
                  endsDay: activeEndsDay(draft.endsDay)
                })
              }
              disabled={draft.serviceDays.length === 0 || draft.origin === draft.destination}
            >
              <Plus size={15} />
              下发点对点航班
            </button>
          </div>
        </section>

        <section className="gm-form-section">
          <strong>未来计划控制</strong>
          <div className="gm-plan-control-grid">
            <Select
              label="未来计划"
              value={activeFlightPlanId}
              values={schedule.flightPlans.map((plan) => plan.id)}
              formatValue={(value) => formatFlightPlanLabel(schedule.flightPlans.find((plan) => plan.id === value))}
              onChange={setFlightPlanId}
            />
            <label>
              停用原因
              <input value={planCancelReason} onChange={(event) => setPlanCancelReason(event.target.value)} />
            </label>
            <button
              disabled={!activeFlightPlan?.active}
              onClick={() => activeFlightPlan && onCancelFlightPlan(activeFlightPlan.id, planCancelReason.trim() || "GM 停用未来航班计划")}
            >
              <X size={14} />
              停用所选未来计划
            </button>
          </div>
        </section>

        <section className="gm-form-section gm-occurrence-control">
          <strong>具体日期航班处置</strong>
          <div className="gm-cancel-form">
            <Select
              label="具体航班"
              value={activeOccurrenceId}
              values={unstartedOccurrences.map((occurrence) => occurrence.id)}
              formatValue={(value) => formatOccurrenceLabel(schedule.occurrences.find((occurrence) => occurrence.id === value))}
              onChange={selectOccurrence}
            />
            <div className={occurrenceWindowHintClassName(activeOccurrenceInTargetWindow)}>
              {formatOccurrenceWindowHint(firstUnopenedOccurrenceDay, activeOccurrence)}
            </div>
            <label>
              运行日
              <input type="number" min={1} value={activeOccurrence?.day ?? cancelDay} onChange={(event) => setCancelDay(Number(event.target.value))} />
            </label>
            <label>
              日期
              <input value={activeOccurrence?.dateIso ?? cancelDateIso} onChange={(event) => setCancelDateIso(event.target.value)} />
            </label>
            <label>
              取消原因
              <input value={legCancelReason} onChange={(event) => setLegCancelReason(event.target.value)} />
            </label>
            <button
              disabled={!activeOccurrenceId || activeOccurrence?.status === "cancelled" || !activeOccurrenceInTargetWindow}
              onClick={() =>
                onCancelFlightLeg({
                  occurrenceId: activeOccurrenceId,
                  day: activeOccurrence?.day ?? cancelDay,
                  dateIso: activeOccurrence?.dateIso ?? cancelDateIso,
                  reason: legCancelReason.trim() || "GM 航段取消"
                })
              }
            >
              取消所选具体航班
            </button>
            <label>
              事件标题
              <input value={occurrenceEventDraft.title} onChange={(event) => setOccurrenceEventDraft((current) => ({ ...current, title: event.target.value }))} />
            </label>
            <Select
              label="事件类型"
              value={occurrenceEventDraft.kind}
              values={OCCURRENCE_EVENT_KIND_VALUES}
              formatValue={(value) => formatOccurrenceKind(value as FlightOccurrenceEventKind)}
              onChange={(kind) => setOccurrenceEventDraft((current) => ({ ...current, kind: kind as FlightOccurrenceEventKind }))}
            />
            <Select
              label="等级"
              value={occurrenceEventDraft.severity}
              values={SEVERITY_VALUES}
              formatValue={(value) => formatSeverity(value as Severity)}
              onChange={(severity) => setOccurrenceEventDraft((current) => ({ ...current, severity: severity as Severity }))}
            />
            <Select
              label="下发基准"
              value={occurrenceEventDraft.trigger.reference}
              values={OCCURRENCE_TRIGGER_REFERENCE_VALUES}
              formatValue={(value) => formatOccurrenceTriggerReference(value as NonNullable<FlightOccurrenceEvent["trigger"]>["reference"])}
              onChange={(reference) => setOccurrenceEventDraft((current) => ({ ...current, trigger: { ...current.trigger, reference: reference as NonNullable<FlightOccurrenceEvent["trigger"]>["reference"] } }))}
            />
            <label>
              偏移分钟
              <input type="number" min={0} max={1440} value={occurrenceEventDraft.trigger.offsetMinutes} onChange={(event) => setOccurrenceEventDraft((current) => ({ ...current, trigger: { ...current.trigger, offsetMinutes: Number(event.target.value) } }))} />
            </label>
            <label className="gm-wide-field">
              事件描述
              <input value={occurrenceEventDraft.description} onChange={(event) => setOccurrenceEventDraft((current) => ({ ...current, description: event.target.value }))} />
            </label>
            <label>
              延误下限
              <input type="number" min={0} value={occurrenceEventDraft.riskProfile.delayMinutesMin} onChange={(event) => setOccurrenceEventDraft((current) => ({ ...current, riskProfile: { ...current.riskProfile, delayMinutesMin: Number(event.target.value) } }))} />
            </label>
            <label>
              延误上限
              <input type="number" min={0} value={occurrenceEventDraft.riskProfile.delayMinutesMax} onChange={(event) => setOccurrenceEventDraft((current) => ({ ...current, riskProfile: { ...current.riskProfile, delayMinutesMax: Number(event.target.value) } }))} />
            </label>
            <label>
              备降风险
              <input type="number" min={0} max={1} step={0.05} value={occurrenceEventDraft.riskProfile.diversionRisk} onChange={(event) => setOccurrenceEventDraft((current) => ({ ...current, riskProfile: { ...current.riskProfile, diversionRisk: Number(event.target.value) } }))} />
            </label>
            <label>
              舆情风险
              <input type="number" min={0} max={1} step={0.05} value={occurrenceEventDraft.riskProfile.newsRisk} onChange={(event) => setOccurrenceEventDraft((current) => ({ ...current, riskProfile: { ...current.riskProfile, newsRisk: Number(event.target.value) } }))} />
            </label>
            <label>
              取消压力
              <input type="number" min={0} max={1} step={0.05} value={occurrenceEventDraft.riskProfile.cancellationPressure} onChange={(event) => setOccurrenceEventDraft((current) => ({ ...current, riskProfile: { ...current.riskProfile, cancellationPressure: Number(event.target.value) } }))} />
            </label>
            <label className="gm-wide-field">
              GM 备注
              <input value={occurrenceEventDraft.reason} onChange={(event) => setOccurrenceEventDraft((current) => ({ ...current, reason: event.target.value }))} />
            </label>
            <div className={occurrenceWindowHintClassName(activeOccurrenceEventInTargetWindow)}>
              {formatOccurrenceEventWindowHint(firstUnopenedOccurrenceDay, activeOccurrence)}
            </div>
            <button
              disabled={!activeOccurrenceId || !activeOccurrenceEventInTargetWindow}
              onClick={() =>
                activeOccurrence &&
                onCreateFlightOccurrenceEvent({
                  occurrenceId: activeOccurrence.id,
                  day: activeOccurrence.day,
                  dateIso: activeOccurrence.dateIso,
                  phase: occurrenceEventPhaseForTriggerReference(occurrenceEventDraft.trigger.reference),
                  ...occurrenceEventDraft
                })
              }
            >
              <ShieldAlert size={14} />
              排定所选突发事件
            </button>
          </div>
        </section>
      </div>

      <div className="gm-table-grid">
        <section className="gm-data-board">
          <div className="gm-board-header">
            <strong>GM 航班计划</strong>
            <label>
              筛选
              <input value={planQuery} onChange={(event) => setPlanQuery(event.target.value)} placeholder="航班号/航段/批次/原因" />
            </label>
          </div>
          <div className="table-wrap compact">
            <table>
              <thead>
                <tr>
                  <th>状态</th>
                  <th>航班</th>
                  <th>航段</th>
                  <th>班期</th>
                  <th>窗口</th>
                  <th>时刻</th>
                  <th>过站</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {filteredFlightPlans.map((plan) => (
                  <tr key={plan.id} className={flightPlanRowClassName(plan.id, activeFlightPlanId)}>
                    <td>
                      <span className={`status-pill ${programStatusTone(plan.active)}`}>{formatProgramStatus(plan.active)}</span>
                    </td>
                    <td>{plan.flightNo}</td>
                    <td>{plan.origin}-{plan.destination}</td>
                    <td>{formatServiceDays(plan.serviceDays)}</td>
                    <td>D{plan.startsDay}-{planEndsDayLabel(plan.endsDay)}</td>
                    <td>{plan.departureTimeLocal}</td>
                    <td>{plan.minimumTurnMinutes}m / P{plan.priority}</td>
                    <td>
                      <div className="row-actions">
                        <button onClick={() => clonePlanToDraft(plan)}>套用</button>
                        <button disabled={!plan.active} onClick={() => onCancelFlightPlan(plan.id, planCancelReason.trim() || "GM 停用未来航班计划")}>
                          停用
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredFlightPlans.length === 0 && (
                  <tr>
                    <td colSpan={8}>没有匹配的 GM 航班计划。</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="gm-data-board">
          <div className="gm-board-header">
            <strong>具体日期航班</strong>
            <label>
              状态
              <select value={occurrenceStatus} onChange={(event) => setOccurrenceStatus(event.target.value as FilterAll<FlightOccurrenceStatus>)}>
                <option value="all">全部</option>
                <option value="scheduled">计划</option>
                <option value="cancelled">取消</option>
                <option value="completed">完成</option>
              </select>
            </label>
            <label>
              筛选
              <input value={occurrenceQuery} onChange={(event) => setOccurrenceQuery(event.target.value)} placeholder="航班/日期/机场/原因" />
            </label>
          </div>
          <div className="table-wrap compact">
            <table>
              <thead>
                <tr>
                  <th>状态</th>
                  <th>具体航班</th>
                  <th>日期</th>
                  <th>航段</th>
                  <th>过站</th>
                  <th>事件</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {filteredOccurrences.map((occurrence) => {
                  const events = schedule.occurrenceEvents.filter((event) => event.occurrenceId === occurrence.id);
                  const cancellation = cancellationByOccurrence.get(occurrence.id);
                  const occurrenceUnstarted = isUnstartedOccurrence(occurrence, firstUnopenedOccurrenceDay);
                  return (
                    <tr key={occurrence.id} className={occurrenceRowClassName(occurrence.id, activeOccurrenceId)}>
                      <td>
                        <span className={`status-pill ${occurrenceStatusTone(occurrence.status)}`}>
                          {formatOccurrenceStatus(occurrence.status)}
                        </span>
                      </td>
                      <td>{occurrence.flightNo}</td>
                      <td>D{occurrence.day} {occurrence.dateIso} / {SERVICE_DAY_LABELS[occurrence.serviceDay]}</td>
                      <td>{occurrence.origin}-{occurrence.destination} {occurrence.departureTimeLocal}</td>
                      <td>{occurrence.minimumTurnMinutes}m / P{occurrence.priority}</td>
                      <td>{formatOccurrenceEventSummary(events, cancellation?.reason ?? occurrence.cancellationReason)}</td>
                      <td>
                        <div className="row-actions">
                          <button disabled={!occurrenceUnstarted} onClick={() => selectOccurrence(occurrence.id)}>选取</button>
                          <button
                            disabled={occurrence.status === "cancelled" || !occurrenceUnstarted}
                            onClick={() =>
                              onCancelFlightLeg({
                                occurrenceId: occurrence.id,
                                day: occurrence.day,
                                dateIso: occurrence.dateIso,
                                reason: legCancelReason.trim() || "GM 航段取消"
                              })
                            }
                          >
                            取消
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredOccurrences.length === 0 && (
                  <tr>
                    <td colSpan={7}>没有匹配的具体日期航班。</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}

type MiniStatTone = "neutral" | "good" | "warn" | "bad";

function cancelledOccurrenceTone(cancelledCount: number): MiniStatTone {
  if (cancelledCount > 0) {
    return "bad";
  }
  return "good";
}

function activeOccurrenceEventTone(eventCount: number): MiniStatTone {
  if (eventCount > 0) {
    return "warn";
  }
  return "good";
}

function awaitingReleaseTone(awaitingReleaseCount: number): MiniStatTone {
  if (awaitingReleaseCount > 0) {
    return "warn";
  }
  return "good";
}

function releasedFlightTone(releasedCount: number): MiniStatTone | undefined {
  if (releasedCount > 0) {
    return "good";
  }
  return undefined;
}

function groundOpsActiveTone(activeCount: number): MiniStatTone | undefined {
  if (activeCount > 0) {
    return "warn";
  }
  return undefined;
}

function groundOpsBlockedTone(blockedCount: number): MiniStatTone {
  if (blockedCount > 0) {
    return "bad";
  }
  return "good";
}

function activeEndsDay(endsDay: number): number | undefined {
  if (endsDay > 0) {
    return endsDay;
  }
  return undefined;
}

function gmFirstUnopenedOccurrenceDay(save: SaveGame | null): number | undefined {
  if (!save) {
    return undefined;
  }
  if (save.activeOpsDay?.status === "open") {
    return save.day + 1;
  }
  return save.day;
}

function isUnstartedOccurrence(occurrence: ScheduleSource["occurrences"][number], firstUnopenedDay: number | undefined): boolean {
  return firstUnopenedDay === undefined || occurrence.day >= firstUnopenedDay;
}

function formatOccurrenceWindowHint(firstUnopenedDay: number | undefined, occurrence: ScheduleSource["occurrences"][number] | undefined): string {
  if (firstUnopenedDay === undefined) {
    return "未绑定玩家存档，可处置所选具体航班。";
  }
  if (occurrence && occurrence.day < firstUnopenedDay) {
    return `当前只能处置 D${firstUnopenedDay} 及以后未开日，所选为 D${occurrence.day}。`;
  }
  return `当前可处置 D${firstUnopenedDay} 及以后未开日。`;
}

function formatOccurrenceEventWindowHint(firstUnopenedDay: number | undefined, occurrence: ScheduleSource["occurrences"][number] | undefined): string {
  if (firstUnopenedDay === undefined) {
    return "未绑定玩家存档，可排定所选具体航班特情。";
  }
  if (occurrence && occurrence.day < firstUnopenedDay) {
    return `当前只能排定 D${firstUnopenedDay} 及以后未开日特情，所选为 D${occurrence.day}。`;
  }
  return `当前可排定 D${firstUnopenedDay} 及以后未开日特情。`;
}

function occurrenceWindowHintClassName(valid: boolean): string {
  return valid ? "gm-window-hint" : "gm-window-hint invalid";
}

function occurrenceEventPhaseForTriggerReference(reference: NonNullable<FlightOccurrenceEvent["trigger"]>["reference"]): FlightOccurrenceEvent["phase"] {
  if (reference === "takeoff") {
    return "enroute";
  }
  return "ground";
}

function flightPlanRowClassName(planId: string, activePlanId: string): string {
  if (planId === activePlanId) {
    return "selected-row";
  }
  return "";
}

function programStatusTone(active: boolean): string {
  if (active) {
    return "good";
  }
  return "bad";
}

function planEndsDayLabel(endsDay: number | undefined): string {
  if (endsDay !== undefined) {
    return `D${endsDay}`;
  }
  return "开放";
}

function occurrenceRowClassName(occurrenceId: string, activeOccurrenceId: string): string {
  if (occurrenceId === activeOccurrenceId) {
    return "selected-row";
  }
  return "";
}

function occurrenceStatusTone(status: FlightOccurrenceStatus): string {
  if (status === "scheduled") {
    return "good";
  }
  if (status === "cancelled") {
    return "bad";
  }
  return "muted";
}
